/*
 * Copyright (c) 2004-2019 Paul Blacknell. All rights reserved.
 * https://github.com/blacknell
 */

import React, {Component, Fragment} from 'react';
import {
    Container, Col, Form,
    FormGroup, Label, Input,
    Button, ButtonToolbar, NavLink,
    Pagination, PaginationLink, PaginationItem, Jumbotron, Alert,
} from 'reactstrap';

import GoogleLogin from 'react-google-login';
import ReactGA from 'react-ga';
import axios from 'axios';

import CalendarFooter from './CalendarFooter'
import confirm from 'reactstrap-confirm';
import sendClientEvent, {eventLevel} from './utils/sendClientEvent';

const API_SUBSCRIBE_PREFIX_REQUEST_PROD = '/api/v1/subscription/';
const API_SUBSCRIBE_PREFIX_REQUEST_LOCAL = 'http://api.sleepylizard.com/api.php?request=subscription/';
const API_AUTHENTICATION_TOKEN = '4NUzB8CmVjzAvgP';

const GA_TIMING_CATEGORY_RESTAPI = 'Sleepylizard API';
const GA_EVENT_CATEGORY_SUBSCRIPTION = 'Subscription';
const GA_EVENT_ACTION_SUCCESS = 'Success';
const GA_EVENT_ACTION_FAILED = 'Failed';
const GA_EVENT_ACTION_VALIDATED = 'Validated';

const subscriptionState = {
    NOT_STARTED: 0,
    PROFILE_VALIDATED: 1,
    SUBSCRIPTION_SUCCESSFUL: 2,
    SUBSCRIPTION_FAILED_ALREADY_SUBSCRIBED: 3,
    SUBSCRIPTION_FAILED_OTHER_REASON: 4,
};

class Subscribe extends Component {

    constructor(props) {
        super(props);

        this.responseGoogle = this.responseGoogle.bind(this);
        this.onSubscribe = this.onSubscribe.bind(this);

        this.state = {
            givenName: '',
            familyName: '',
            name: '',
            email: '',
            profileStep: subscriptionState.NOT_STARTED,
        };
    }

    /**
     *
     * @returns {Promise<void>}
     */
    async onSubscribe() {
        const now = performance.now();

        try {
            const restRequestPrefix = window.location.host === 'localhost:3000' ? API_SUBSCRIBE_PREFIX_REQUEST_LOCAL : API_SUBSCRIBE_PREFIX_REQUEST_PROD;
            const res = await axios.post(
                restRequestPrefix + this.state.email,
                {
                    givenName: this.state.givenName,
                    familyName: this.state.familyName,
                    name: this.state.name,
                },
                {
                    headers: {
                        'Accept': 'application/json',
                        'Authentication-Token': API_AUTHENTICATION_TOKEN,
                    },
                    withCredentials: true,
                    timeout: 5000,

                });
            ReactGA.timing({
                category: GA_TIMING_CATEGORY_RESTAPI,
                variable: 'subscribe.put',
                value: parseInt((performance.now() - now)), // in milliseconds
            });
            ReactGA.event({
                category: GA_EVENT_CATEGORY_SUBSCRIPTION,
                action: GA_EVENT_ACTION_SUCCESS,
            });

            this.setState({profileStep: subscriptionState.SUBSCRIPTION_SUCCESSFUL});

            // send the subscription to the logger
            sendClientEvent(eventLevel.notice, 'New subscription',
                {
                    subscriber:
                        {
                            email: this.state.email,
                            givenName: this.state.givenName,
                            familyName: this.state.familyName,
                            name: this.state.name,
                            uid: res.data.gaUserId,
                            expires: res.data.expires,
                        },
                },
            );

        } catch (error) {

            ReactGA.event({
                category: GA_EVENT_CATEGORY_SUBSCRIPTION,
                action: GA_EVENT_ACTION_FAILED,
            });

            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                console.dir(error.response);

                // send the error to the logger
                sendClientEvent(eventLevel.notice, 'Failed subscription',
                    {
                        subscriber:
                            {
                                email: this.state.email,
                                givenName: this.state.givenName,
                                familyName: this.state.familyName,
                                name: this.state.name,
                            },
                        error: error.response.data,
                    },
                );

                switch (error.response.status) {
                    case 400: {
                        this.setState({profileStep: subscriptionState.SUBSCRIPTION_FAILED_ALREADY_SUBSCRIBED});
                        break;
                    }
                    default: {
                        this.setState({profileStep: subscriptionState.SUBSCRIPTION_FAILED_OTHER_REASON});
                        await confirm(
                            {
                                title: 'Something went wrong at our end',
                                message: (
                                    <Fragment>Although you successfully signed in with your Google ID, access to sleepylizard failed. Here is the error message.
                                        <Alert className='mt-2 mb-2' color='danger'>{error.response.data.code} - {error.response.data.error}</Alert>
                                        Please contact <NavLink className="inlineLink" href='mailto:support@sleepylizard.com'>support@sleepylizard.com</NavLink> if this continues to be a problem.
                                    </Fragment>
                                ),
                                confirmColor: 'primary',
                                cancelText: null,
                            },
                        );
                        break;
                    }
                }
            } else if (error.request) {
                // The request was made but no response was received
                // `error.request` is an instance of XMLHttpRequest in the browser and an instance of
                // http.ClientRequest in node.js
                console.error(error.request);
            } else {
                // Something happened in setting up the request that triggered an Error
                console.error('Error', error.message);
            }
        }
    }

    /**
     *
     * @param response
     */
    responseGoogle(response) {
        if (typeof response.profileObj !== 'undefined') {
            this.setState({givenName: response.profileObj.givenName});
            this.setState({familyName: response.profileObj.familyName});
            this.setState({name: response.profileObj.name});
            this.setState({email: response.profileObj.email});
            this.setState({profileStep: subscriptionState.PROFILE_VALIDATED});
            ReactGA.event({
                category: GA_EVENT_CATEGORY_SUBSCRIPTION,
                action: GA_EVENT_ACTION_VALIDATED,
            });
        }
    }

    render() {
        ReactGA.pageview('/subscribe');

        return (
            <Container>
                <Form>
                    <Col>
                        <Jumbotron>
                            <h1 className="display-3">Subscribe to sleepylizard</h1>
                            <p className="lead">Subscribing is a simple 2-step process and is all done with your Google ID.</p>
                            <hr className="my-2"/>
                            <p>This ensures your personal information is kept secure and in accordance with our Privacy Policy.</p>
                        </Jumbotron>
                        <Pagination aria-label="Page navigation example">
                            <PaginationItem first="true"
                                            disabled={this.state.profileStep !== subscriptionState.NOT_STARTED}
                                            active={this.state.profileStep === subscriptionState.NOT_STARTED}>
                                <PaginationLink href="#">
                                    Step 1
                                </PaginationLink>
                            </PaginationItem>
                            <PaginationItem disabled={this.state.profileStep !== subscriptionState.PROFILE_VALIDATED}
                                            active={this.state.profileStep === subscriptionState.PROFILE_VALIDATED}>
                                <PaginationLink href="#">
                                    Step 2
                                </PaginationLink>
                            </PaginationItem>
                            <PaginationItem
                                disabled={this.state.profileStep !== subscriptionState.SUBSCRIPTION_FAILED_OTHER_REASON &&this.state.profileStep !== subscriptionState.SUBSCRIPTION_FAILED_ALREADY_SUBSCRIBED && this.state.profileStep !== subscriptionState.SUBSCRIPTION_SUCCESSFUL}
                                active={this.state.profileStep === subscriptionState.SUBSCRIPTION_FAILED_OTHER_REASON ||this.state.profileStep === subscriptionState.SUBSCRIPTION_FAILED_ALREADY_SUBSCRIBED || this.state.profileStep === subscriptionState.SUBSCRIPTION_SUCCESSFUL}>
                                <PaginationLink href="#">
                                    Finish
                                </PaginationLink>
                            </PaginationItem>
                        </Pagination>

                        {this.state.profileStep === subscriptionState.NOT_STARTED &&
                        <Fragment>
                            <div className="mb-2">Sign in with your Google account in order to validate your email address.
                                This is the Google account that will be used to retrieve the Google calendars you have access to.
                            </div>
                            <GoogleLogin
                                clientId='1084547316944-camnso5qan3470ptpi0m79ptn8s4tq9j.apps.googleusercontent.com'
                                onSuccess={this.responseGoogle}
                                onFailure={this.responseGoogle}
                                cookiePolicy={'single_host_origin'}
                            />
                        </Fragment>}

                        {this.state.profileStep === subscriptionState.PROFILE_VALIDATED &&
                        <Fragment>
                            <FormGroup>
                                <Label for="name">Name</Label>
                                <Input id="name" type="text" defaultValue={this.state.name} disabled/>
                            </FormGroup>
                            <FormGroup>
                                <Label for="email">Email</Label>
                                <Input id="email" type="email" defaultValue={this.state.email} disabled/>
                            </FormGroup>
                            <div className="mb-2">Confirm if you want to subscribe to sleepylizard. If you subscribe we
                                will only store your Google Profile in accordance with our <NavLink className="inlineLink" href='/help/docs/privacy-policy/'>Privacy
                                    Policy</NavLink>.
                            </div>
                            <Button color='primary' onClick={() => this.onSubscribe()}>Subscribe</Button>
                        </Fragment>}

                        {this.state.profileStep === subscriptionState.SUBSCRIPTION_SUCCESSFUL &&
                        <Fragment>
                            <div className="mb-2">Your subscription was successful.
                                Please continue to <NavLink className="inlineLink" href='/'>setup your Family Calendars</NavLink>.
                            </div>
                        </Fragment>}

                        {this.state.profileStep === subscriptionState.SUBSCRIPTION_FAILED_OTHER_REASON &&
                        <Fragment>
                            <div className="mb-2">Your subscription was not successful.
                                Please contact <NavLink className="inlineLink" href='mailto:support@sleepylizard.com'>support@sleepylizard.com</NavLink> for assistance.
                            </div>
                        </Fragment>}

                        {this.state.profileStep === subscriptionState.SUBSCRIPTION_FAILED_ALREADY_SUBSCRIBED &&
                        <Fragment>
                            <div className="mb-2">You already have a subscription.
                                Please sign in to your <NavLink className="inlineLink" href='/'>Family Calendar</NavLink>.
                            </div>
                        </Fragment>}

                    </Col>
                </Form>

                <div className="CalendarButtonToolbar">
                    <ButtonToolbar>
                        <Button size="sm" onClick={() => window.open('/help/docs/intro/')}>Help</Button>&nbsp;
                        <Button size="sm" onClick={() => window.open('/help/docs/privacy-policy/')}>Privacy</Button>
                    </ButtonToolbar>
                </div>
                <CalendarFooter/>
            </Container>

        );
    }
}

export default Subscribe;